import React, { useEffect, useRef, useState } from 'react'
import {
	Box, Card, CardContent, Typography, Table, TableHead, TableBody, TableRow, TableCell,
	Checkbox, Grid
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { ButtonIconSeeker, IconSeeker } from '@ui-seeker/core'
import { AppCardTitle, AppSelectionBar, AppSelectionBarButton } from '@components'
import { dsColorNew } from "@palette"


const DEMO_EXPORTS = [
	{
		fileName: 'grouped_export_spain_20250210_0800.csv',
		created: '10 Feb 2025 08:15',
		exportDate: '10 Feb 2025 08:22',
		exported: true,
		hasUrl: true,
	},
	{
		fileName: 'grouped_export_france_20250210_1000.csv',
		created: '10 Feb 2025 10:02',
		exportDate: null,
		exported: false,
		hasUrl: false,
		selected: true,
	},
	{
		fileName: 'grouped_export_italy_20250209_0900.csv',
		created: '09 Feb 2025 09:00',
		exportDate: null,
		exported: false,
		hasUrl: false,
	},
]


const noop = () => {}

// Same cells as Pages/ReportsAlerts/ExportsGrouping/Table (AppDataTable with selectable rows)
// True while the real exports table is on the page (its data arrived after the tour started)
function useRealTableShown(rootRef) {
	const [shown, setShown] = useState(false)

	useEffect(() => {
		const check = () => setShown([...document.querySelectorAll('[class*="MUIDataTable-paper"]')].some(paper => !rootRef.current?.contains(paper)))
		check()
		const id = setInterval(check, 500)
		return () => clearInterval(id)
	}, [])

	return shown
}


export default function GroupingExportsDemoData() {
	const rootRef = useRef(null)
	// the real table takes the demo's place, so there is one table and one selection bar
	const realTableShown = useRealTableShown(rootRef)
	if (realTableShown) return null

	return <Grid container spacing={2} ref={rootRef}>
		<Grid item xs={12}>
			<Card>
				<CardContent sx={{ p: 2 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
						<AppCardTitle icon="layer-group" title="Grouped Exports List 09 Feb 2025 - 10 Feb 2025"/>
						<Typography sx={{ fontSize: 12, color: dsColorNew.textSubtle }}>
							{DEMO_EXPORTS.length} exports
						</Typography>
					</Box>

					<Box sx={{ overflowX: 'auto' }}>
						{/* div-based (no <tbody>): the page shows the demo only while no `.MuiTable-root tbody tr` is on screen, so the demo must not match it itself */}
						<Table size="small" component="div">
							<TableHead component="div">
								<TableRow component="div" sx={{ '& .MuiTableCell-head': { whiteSpace: 'nowrap' } }}>
									<TableCell component="div" padding="checkbox">
										<Checkbox size="small" sx={{ color: alpha(dsColorNew.white, 0.7), '&.Mui-checked': { color: dsColorNew.white } }}/>
									</TableCell>
									<TableCell component="div">File Name</TableCell>
									<TableCell component="div" align="center">Creation Date</TableCell>
									<TableCell component="div" align="center">Export Date</TableCell>
									<TableCell component="div" align="center">Exported</TableCell>
									<TableCell component="div" align="center">Info</TableCell>
								</TableRow>
							</TableHead>
							<TableBody component="div">
								{DEMO_EXPORTS.map((r, idx) => (
									<TableRow component="div" key={r.fileName} sx={{
										backgroundColor: idx % 2 === 0 ? dsColorNew.white : dsColorNew.tableRowAlt,
										'&:hover': { backgroundColor: dsColorNew.hoverBgLight },
										'& .MuiTableCell-body': { py: 1.25, fontSize: 13 },
									}}>
										<TableCell component="div" padding="checkbox">
											<Checkbox size="small" checked={!!r.selected} tabIndex={-1} sx={{ color: dsColorNew.webBlue500, '&.Mui-checked': { color: dsColorNew.webBlue500 } }}/>
										</TableCell>

										<TableCell component="div">
											<Typography sx={{ fontSize: 13, fontWeight: 'bold' }}>{r.fileName}</Typography>
										</TableCell>

										<TableCell component="div" align="center">
											<Typography sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{r.created}</Typography>
										</TableCell>

										<TableCell component="div" align="center">
											<Typography sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{r.exportDate || '—'}</Typography>
										</TableCell>

										<TableCell component="div" align="center">
											<IconSeeker
												icon={r.exported ? 'square-check' : 'square-xmark'}
												sx={{ color: r.exported ? dsColorNew.successText : dsColorNew.dangerRed, fontSize: 24 }}
											/>
										</TableCell>

										<TableCell component="div" align="center">
											<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-evenly' }}>
												<ButtonIconSeeker icon="circle-info" iconWeight="light" tooltip="More info"/>
												{r.hasUrl && <ButtonIconSeeker icon="down-to-bracket" iconWeight="light" tooltip="Export URL"/>}
											</Box>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</Box>

					{/* The floating selection bar of the real table (Admin/GroupedExports/Table/Toolbar), with one row selected; nothing acts */}
					<AppSelectionBar data-tour-id="tour-groups-demo-bar" count={DEMO_EXPORTS.filter(r => r.selected).length} onClear={noop}>
						<AppSelectionBarButton data-tour-id="tour-force-export" icon="cloud-arrow-up" label="Force export" title="Force multiple file exports" tabIndex={-1}/>
					</AppSelectionBar>
				</CardContent>
			</Card>
		</Grid>
	</Grid>
}
